import { MouseEvent } from 'react';

/**
 * Supported deeplink platforms
 */
enum DeeplinkPlatforms {
	fb,
	messenger,
	sms,
	defaultEmailClient,
}

interface DeeplinkCallback {
	(e: MouseEvent | null): void;
}

/**
 * A map of supported deeplink openers
 */
type DeeplinkMap = {
	[Property in keyof typeof DeeplinkPlatforms]: DeeplinkCallback;
};

/**
 * formats a link to be shared via deep link
 */
export const deepLinks = ({
	link,
	message = '',
	subject = '',
}): DeeplinkMap => {
	const encodedQuery = encodeURIComponent(link);
	const fullEncodedQuery = encodeURIComponent(`${message}\n${link}`);
	const encodedSubject = encodeURIComponent(subject);

	return {
		fb: makeOnClick(
			`https://www.facebook.com/dialog/share?app_id=${process.env.FB_APP_ID}&display=page&href=${encodedQuery}&redirect_uri=${encodedQuery}`
		),
		messenger: makeOnClick(
			`fb-messenger://share/?link=${encodedQuery}&app_id=${process.env.FB_APP_ID}`
		),
		sms: makeOnClick(
			`sms://${isAppleUA() ? '&' : '?'}body=${fullEncodedQuery}`
		),
		defaultEmailClient: makeOnClick(
			`mailto:?body=${fullEncodedQuery}&subject=${encodedSubject}`
		),
	};
};

export const deepLinkRedirect = ({
	platform,
	link,
	message = '',
	subject = '',
}) => {
	const callbacks = deepLinks({ link, message, subject });

	if (callbacks[platform]) {
		return callbacks[platform](null);
	}

	// fallback to just opening the link directly
	return window.open(link, '_blank');
};

const isAppleUA = () =>
	navigator.userAgent.match(/Macintosh/i) ||
	navigator.userAgent.match(/iPad/i) ||
	navigator.userAgent.match(/iPhone/i) ||
	navigator.userAgent.match(/iPod/i);

/**
 * Makes a callback to open a deeplink
 */
const makeOnClick = (data: string): DeeplinkCallback => (e) => {
	window.open(data, '_blank');
};
